import { useState } from 'react';
import toast from 'react-hot-toast';
import DataTable from 'react-data-table-component';

import Modal from '../Modal';
import TableLoader from '../TableLoader';
import AddLicenseTypeForm from '../AddLicenseTypeForm';
import UpdateLicenseTypeForm from '../UpdateLicenseTypeForm';
import ConfirmPrompt from '../ConfirmPrompt';

import useLicenseTypes from '../../hooks/useLicenseTypes';

import licenseTypeService from '../../services/licenseTypeService';
import { formatDateToSydneyTimeZone } from '../../utils/dateUtils';

const ExpandableContent = ({ data }) => (
  <div className='flex flex-col gap-2 p-4 text-sm border-b'>
    <p>Description: {data.description}</p>
  </div>
)

const AdminLicenseTypes = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const [selectedLicenseType, setSelectedLicenseType] = useState(null);
  const [blockingUsages, setBlockingUsages] = useState([]);

  const [isAddLicenseTypeModalOpen, setIsAddLicenseTypeModalOpen] = useState(false);
  const [isUpdateLicenseTypeModalOpen, setIsUpdateLicenseTypeModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);

  const { data, isLoading, mutate } = useLicenseTypes({
    page,
    perPage,
    sortField,
    sortOrder,
    showArchived
  });

  const licenseTypes = data?.licenseTypes;
  const licenseTypeCount = data?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleAddClicked = () => {
    setIsAddLicenseTypeModalOpen(true);
  }

  const handleUpdateClicked = (row) => {
    setSelectedLicenseType(row);
    setIsUpdateLicenseTypeModalOpen(true);
  };

  const handleDeleteClicked = (row) => {
    setSelectedLicenseType(row);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const handleAddLicenseTypeFormSubmitted = async (payload) => {
    try {
      setIsCreating(true);

      await licenseTypeService.createLicenseType(payload);
      mutate();
      setIsAddLicenseTypeModalOpen(false);
    } catch (error) {
      toast.error('Failed to create license type');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateLicenseTypeFormSubmitted = async (payload) => {
    try {
      setIsUpdating(true);

      await licenseTypeService.updateLicenseType({
        licenseTypeId: selectedLicenseType._id,
        updateData: payload
      });
      mutate();
      setIsUpdateLicenseTypeModalOpen(false);
    } catch (error) {
      toast.error('Failed to update license type');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleArchiveLicenseType = async (licenseType) => {
    try {
      setIsArchiving(true);

      await licenseTypeService.updateLicenseType({
        licenseTypeId: licenseType._id,
        updateData: { archived: !licenseType.archived }
      });
      mutate();
      toast.success(licenseType.archived ? 'License type restored' : 'License type archived');
    } catch (error) {
      toast.error('Failed to update license type');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDeleteLicenseType = async (licenseType) => {
    try {
      setIsDeleting(true);

      await licenseTypeService.deleteLicenseType(licenseType._id);
      mutate();
      setIsConfirmDeleteModalOpen(false);

      toast.success('License type deleted');
    } catch (error) {
      const usages = error?.response?.data?.data;
      if (usages) {
        setBlockingUsages(usages);
        setIsBlockedModalOpen(true);
      } else {
        toast.error(error?.response?.data?.message || 'Failed to delete license type');
      }
      setIsConfirmDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      name: 'HRWL Code',
      selector: row => row.hrwlCode,
      sortable: true,
      sortField: 'hrwlCode'
    },
    {
      name: 'Name',
      selector: row => row.name,
      grow: 2,
      sortable: true,
      sortField: 'name'
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleUpdateClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Update
          </button>

          <button
            onClick={() => handleArchiveLicenseType(row)}
            disabled={isArchiving}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-gray-500 text-white text-nowrap'
          >
            {row.archived ? 'Restore' : 'Archive'}
          </button>

          <button
            onClick={() => handleDeleteClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className='flex flex-col'>
        <div className='flex justify-end px-4 pt-4'>
          <button
            onClick={() => { setShowArchived(!showArchived); setPage(1); }}
            className='text-xs text-gray-500 underline'
          >
            {showArchived ? 'Show active' : 'Show archived'}
          </button>
        </div>

        <DataTable
          columns={columns}
          data={licenseTypes}
          expandableRows
          expandableRowsComponent={ExpandableContent}
          striped
          highlightOnHover
          pointerOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={licenseTypeCount}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

        {!showArchived && (
          <div className='px-4 pb-4'>
            <button
              className='bg-ase-blue h-[48px] px-4 font-semibold text-white rounded-md flex items-center justify-center w-fit'
              onClick={handleAddClicked}
            >
              Add
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={isAddLicenseTypeModalOpen}>
        <AddLicenseTypeForm
          onSubmit={handleAddLicenseTypeFormSubmitted}
          onCloseClicked={e => setIsAddLicenseTypeModalOpen(false)}
          isLoading={isCreating}
        />
      </Modal>

      <Modal isOpen={isUpdateLicenseTypeModalOpen}>
        <UpdateLicenseTypeForm
          licenseType={selectedLicenseType}
          isLoading={isUpdating}
          onSubmit={handleUpdateLicenseTypeFormSubmitted}
          onCloseClicked={e => setIsAddLicenseTypeModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete the ${selectedLicenseType?.name} license type?`}
          onConfirm={() => handleDeleteLicenseType(selectedLicenseType)}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
          isLoading={isDeleting}
        />
      </Modal>

      <Modal isOpen={isBlockedModalOpen}>
        <div className='bg-white rounded-lg border border-ase-blue w-full md:w-auto md:max-w-[600px]'>
          <h2 className='text-lg font-semibold bg-ase-blue py-2 px-4 text-white'>
            Cannot Delete License Type
          </h2>

          <div className='px-4 py-4 flex flex-col gap-4'>
            <p className='text-sm font-semibold'>
              <span>{selectedLicenseType?.name}</span> is referenced by {blockingUsages.length} license(s) belonging to the following labourer(s):
            </p>

            <ul className='text-sm border border-ase-blue rounded-md divide-y max-h-60 overflow-y-auto'>
              {blockingUsages.map((u, i) => (
                <li key={i} className='px-3 py-2 flex justify-between gap-4'>
                  <span>{u.labourerName}</span>
                  <span className='text-gray-500 text-nowrap'>{u.expirationDate ? formatDateToSydneyTimeZone(u.expirationDate, 'dd MMM yyyy') : 'No expiry'}</span>
                </li>
              ))}
            </ul>

            <p className='text-sm text-gray-500'>Archive the type to hide it from new uploads while preserving existing records.</p>

            <div className='flex items-center gap-2'>
              <button
                onClick={() => { setIsBlockedModalOpen(false); handleArchiveLicenseType(selectedLicenseType); }}
                disabled={isArchiving}
                className='bg-ase-blue text-white font-semibold px-2 py-1 text-sm rounded-md'
              >
                Archive instead
              </button>
              <button
                onClick={() => setIsBlockedModalOpen(false)}
                className='border border-ase-blue text-ase-blue font-semibold px-2 py-1 text-sm rounded-md'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AdminLicenseTypes;
